import { createAsyncThunk } from "@reduxjs/toolkit";
import { MemberResponse } from "../contracts/MemberResponse";
import { GroupResponse } from "../contracts/GroupResponse";
import { CreateMemberRequest } from "../contracts/CreateMemberRequest";
import { UpdateMemberRequest } from "../contracts/UpdateMemberRequest";
import { CreateGroupRequest } from "../contracts/CreateGroupRequest";
import { UpdateGroupRequest } from "../contracts/UpdateGroupRequest";
import { Member } from "../models/Member";
import { Group } from "../models/Group";
import { MemberResponseMapper } from "../mappers/MemberResponseMapper";
import { GroupResponseMapper } from "../mappers/GroupResponseMapper";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import axiosInstance from "../../axiosInstance/axiosInstance";

const memberMapper = new MemberResponseMapper();
const groupMapper = new GroupResponseMapper();

/**
 * Async thunk for fetching all members for the current instructor's organization
 */
export const fetchMembers = createAsyncThunk<
  Member[],
  void,
  { rejectValue: ApiErrorResponse }
>("members/fetchMembers", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MemberResponse[]>("/members");
    return response.data.map((memberResponse) =>
      memberMapper.toMember(memberResponse)
    );
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to fetch members"));
  }
});

/**
 * Async thunk for fetching a single member by ID
 */
export const fetchMemberById = createAsyncThunk<
  Member,
  string,
  { rejectValue: ApiErrorResponse }
>("members/fetchMemberById", async (memberId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MemberResponse>(
      `/members/${memberId}`
    );
    return memberMapper.toMember(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to fetch member"));
  }
});

/**
 * Async thunk for creating a new member
 */
export const createMember = createAsyncThunk<
  Member,
  CreateMemberRequest,
  { rejectValue: ApiErrorResponse }
>(
  "members/createMember",
  async (request: CreateMemberRequest, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<MemberResponse>(
        "/members",
        request
      );
      return memberMapper.toMember(response.data);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to create member"));
    }
  }
);

/**
 * Async thunk for updating an existing member
 */
export const updateMember = createAsyncThunk<
  Member,
  { id: string; request: UpdateMemberRequest },
  { rejectValue: ApiErrorResponse }
>(
  "members/updateMember",
  async ({ id, request }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put<MemberResponse>(
        `/members/${id}`,
        request
      );
      return memberMapper.toMember(response.data);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to update member"));
    }
  }
);

/**
 * Async thunk for deleting a member
 */
export const deleteMember = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("members/deleteMember", async (memberId: string, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`/members/${memberId}`);
    return memberId;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to delete member"));
  }
});

/**
 * Async thunk for fetching all groups for the current instructor's organization
 */
export const fetchGroups = createAsyncThunk<
  Group[],
  void,
  { rejectValue: ApiErrorResponse }
>("members/fetchGroups", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<GroupResponse[]>("/groups");
    return response.data.map((groupResponse) =>
      groupMapper.toGroup(groupResponse)
    );
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to fetch groups"));
  }
});

/**
 * Async thunk for fetching a single group by ID
 */
export const fetchGroupById = createAsyncThunk<
  Group,
  string,
  { rejectValue: ApiErrorResponse }
>("members/fetchGroupById", async (groupId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<GroupResponse>(
      `/groups/${groupId}`
    );
    return groupMapper.toGroup(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to fetch group"));
  }
});

/**
 * Async thunk for creating a new group
 */
export const createGroup = createAsyncThunk<
  Group,
  CreateGroupRequest,
  { rejectValue: ApiErrorResponse }
>(
  "members/createGroup",
  async (request: CreateGroupRequest, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<GroupResponse>(
        "/groups",
        request
      );
      return groupMapper.toGroup(response.data);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to create group"));
    }
  }
);

/**
 * Async thunk for updating an existing group
 */
export const updateGroup = createAsyncThunk<
  Group,
  { id: string; request: UpdateGroupRequest },
  { rejectValue: ApiErrorResponse }
>(
  "members/updateGroup",
  async ({ id, request }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put<GroupResponse>(
        `/groups/${id}`,
        request
      );
      return groupMapper.toGroup(response.data);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to update group"));
    }
  }
);

/**
 * Async thunk for deleting a group
 */
export const deleteGroup = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("members/deleteGroup", async (groupId: string, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`/groups/${groupId}`);
    return groupId;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to delete group"));
  }
});

/**
 * Async thunk for adding a member to a group
 */
export const addMemberToGroup = createAsyncThunk<
  { groupId: string; memberId: string },
  { groupId: string; memberId: string },
  { rejectValue: ApiErrorResponse }
>(
  "members/addMemberToGroup",
  async ({ groupId, memberId }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`/groups/${groupId}/members`, { memberId });
      return { groupId, memberId };
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to add member to group"));
    }
  }
);

/**
 * Async thunk for removing a member from a group
 */
export const removeMemberFromGroup = createAsyncThunk<
  { groupId: string; memberId: string },
  { groupId: string; memberId: string },
  { rejectValue: ApiErrorResponse }
>(
  "members/removeMemberFromGroup",
  async ({ groupId, memberId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/groups/${groupId}/members/${memberId}`);
      return { groupId, memberId };
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to remove member from group"));
    }
  }
);

/**
 * Async thunk for fetching members belonging to a specific group
 */
export const fetchGroupMembers = createAsyncThunk<
  Member[],
  string,
  { rejectValue: ApiErrorResponse }
>(
  "members/fetchGroupMembers",
  async (groupId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<MemberResponse[]>(
        `/groups/${groupId}/members`
      );
      return response.data.map((memberResponse) =>
        memberMapper.toMember(memberResponse)
      );
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to fetch group members"));
    }
  }
);
